/**
 * POPSTAR auth screen — the anon → named upgrade path. Three flows share one
 * frosted glass card: sign up, log in, and reset password. It is purely
 * presentational and human-timescale: it only talks to the {@link GameServices}
 * account surface (`signUp` / `logIn` / `resetPassword` / `signInWithGoogle`) on
 * a discrete submit, holds local form state, and surfaces success/error text. It
 * never touches the game loop, holds no per-frame state, and does not know about
 * screens above it — the parent ({@link import('./NetworkShell').NetworkShell})
 * decides what happens on success via {@link AuthScreenProps.onAuthed} /
 * {@link AuthScreenProps.onBack}.
 *
 * Layout follows the legacy POPSTAR splash (welcome → register / login / reset):
 * a centered card with an explicit sign-up / log-in mode toggle up top, the
 * "forgot password?" reset as a sub-flow off the log-in side, and a one-tap
 * "Continue with Google" route. The reset flow has its own "back to log in"
 * affordance so the toggle never strands the user.
 *
 * Account methods are ANON-ALLOWED (they ARE the upgrade path), so this screen is
 * reachable by anonymous users. On the offline LocalBackend they throw
 * {@link import('../services/types').RequiresAccountError}; we render that error
 * inline rather than crashing.
 *
 * Two routes share the same upgrade: the email/password form (sign up / log in /
 * reset) and a one-tap "Continue with Google" button ({@link GameServices.signInWithGoogle}).
 * Google works in BOTH sign-up and log-in modes — the provider itself decides
 * whether to create or sign in — so the button is shown for those two modes and
 * hidden on the reset flow. It drives the same `busy`/`error` state as the form.
 */
import { useCallback, useState } from 'react';
import type { GameServices } from '../services/types';

/** Which of the three flows the single card is currently showing. */
type AuthMode = 'login' | 'signup' | 'reset';

interface AuthScreenProps {
  services: GameServices;
  /** Called after a successful sign-up / log-in (the user is now `named`). */
  onAuthed: () => void;
  /** Dismiss the screen without authenticating (back to the network shell). */
  onBack: () => void;
}

/** Human-readable error text from any thrown value (Error, string, unknown). */
function errorText(err: unknown): string {
  if (err instanceof Error) return err.message;
  return 'Something went wrong. Please try again.';
}

const MODE_TITLE: Record<AuthMode, string> = {
  login: 'Welcome back',
  signup: 'Join POPSTAR',
  reset: 'Reset password',
};

/** Subhead under the title — sets the tone per flow. */
const MODE_BLURB: Record<AuthMode, string> = {
  login: 'Sign in to pick up your XP, stickers, tokens and wheels.',
  signup:
    'A named account unlocks XP, stickers, tokens, wheels and your profile — and carries your high score over.',
  reset: "We'll email you a secure link to set a new password.",
};

/** Submit-button label per flow (and a shared busy state). */
const SUBMIT_LABEL: Record<AuthMode, string> = {
  signup: 'Create account',
  login: 'Log in',
  reset: 'Send reset link',
};

export function AuthScreen({ services, onAuthed, onBack }: AuthScreenProps) {
  const [mode, setMode] = useState<AuthMode>('signup');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  /** Switch flows, clearing transient feedback so it doesn't bleed across modes. */
  const switchMode = useCallback((next: AuthMode) => {
    setMode(next);
    setError(null);
    setNotice(null);
  }, []);

  const submit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      if (busy) return;
      setBusy(true);
      setError(null);
      setNotice(null);
      try {
        if (mode === 'signup') {
          await services.signUp(email.trim(), password, name.trim());
          onAuthed();
        } else if (mode === 'login') {
          await services.logIn(email.trim(), password);
          onAuthed();
        } else {
          await services.resetPassword(email.trim());
          setNotice('Check your inbox for a reset link.');
        }
      } catch (err) {
        setError(errorText(err));
      } finally {
        setBusy(false);
      }
    },
    [busy, mode, services, email, password, name, onAuthed],
  );

  /**
   * One-tap Google upgrade — links the Google credential to the current anon uid
   * (same anon→named transition as the form), then advances via {@link onAuthed}.
   * Shares the form's `busy`/`error` state so the two routes can't fire at once.
   */
  const signInGoogle = useCallback(async () => {
    if (busy) return;
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      await services.signInWithGoogle();
      onAuthed();
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  }, [busy, services, onAuthed]);

  const isReset = mode === 'reset';

  return (
    <div className="screen network-screen auth-screen">
      <style>{AUTH_CSS}</style>

      <header className="network-head">
        <button className="btn btn-ghost network-back" onClick={onBack} type="button">
          ‹ Back
        </button>
        <h1 className="network-title">{MODE_TITLE[mode]}</h1>
      </header>

      <p className="auth-tagline">{MODE_BLURB[mode]}</p>

      <form className="panel auth-card" onSubmit={submit}>
        {/* Mode toggle — the legacy "register / login" choice, made explicit. On
            the reset sub-flow it's swapped for a single "back to log in" rail. */}
        {isReset ? (
          <button
            type="button"
            className="auth-reset-back"
            onClick={() => switchMode('login')}
          >
            ‹ Back to log in
          </button>
        ) : (
          <div className="auth-tabs" role="tablist" aria-label="Sign up or log in">
            <button
              type="button"
              role="tab"
              aria-selected={mode === 'signup'}
              className={`auth-tab${mode === 'signup' ? ' active' : ''}`}
              onClick={() => switchMode('signup')}
            >
              Sign up
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={mode === 'login'}
              className={`auth-tab${mode === 'login' ? ' active' : ''}`}
              onClick={() => switchMode('login')}
            >
              Log in
            </button>
          </div>
        )}

        {mode === 'signup' && (
          <label className="field">
            <span className="field-label">Display name</span>
            <input
              className="field-input"
              type="text"
              autoComplete="nickname"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Your stage name"
              required
              maxLength={24}
            />
          </label>
        )}

        <label className="field">
          <span className="field-label">Email</span>
          <input
            className="field-input"
            type="email"
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@example.com"
            required
          />
        </label>

        {!isReset && (
          <label className="field">
            <span className="field-label">Password</span>
            <input
              className="field-input"
              type="password"
              autoComplete={mode === 'signup' ? 'new-password' : 'current-password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              required
              minLength={6}
            />
          </label>
        )}

        {/* "Forgot password?" sits beside the password field on the log-in flow. */}
        {mode === 'login' && (
          <div className="auth-forgot-row">
            <button
              type="button"
              className="link auth-forgot"
              onClick={() => switchMode('reset')}
            >
              Forgot password?
            </button>
          </div>
        )}

        {error && (
          <p className="auth-error" role="alert">
            {error}
          </p>
        )}
        {notice && (
          <p className="auth-notice" role="status">
            {notice}
          </p>
        )}

        <button className="btn btn-primary auth-submit" type="submit" disabled={busy}>
          {busy ? 'Working…' : SUBMIT_LABEL[mode]}
        </button>

        {!isReset && (
          <>
            <div className="auth-divider" aria-hidden>
              <span>or</span>
            </div>
            <button
              className="btn btn-ghost auth-google"
              type="button"
              onClick={() => void signInGoogle()}
              disabled={busy}
            >
              <span className="auth-google-glyph" aria-hidden>
                G
              </span>
              Continue with Google
            </button>
          </>
        )}
      </form>

      <p className="auth-fineprint">
        {mode === 'signup'
          ? 'Already a star? Use the Log in tab above.'
          : mode === 'login'
            ? 'New here? Switch to Sign up to create your account.'
            : 'Remembered it? Head back to log in.'}
      </p>
    </div>
  );
}

/**
 * Screen-scoped styles for the auth card's bespoke layout (mode toggle, the
 * "forgot password" rail, the "or" divider and the Google glyph). Nested under
 * `.auth-screen` so they only apply here. Every value reads a design-system
 * custom property from src/index.css (re-mapped to the POPSTAR purple/magenta/
 * gold palette under `.network-screen`) — no ad-hoc colours. Shared primitives
 * (`.btn`, `.btn-ghost`, `.field`, `.link`, `.panel`) come from the global sheet.
 */
const AUTH_CSS = `
.auth-screen .auth-card {
  gap: 0; /* .field carries its own bottom margin; toggle adds its own below */
}

/* ---- Glass segmented mode toggle (sign up / log in) ---- */
.auth-screen .auth-tabs {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 4px;
  padding: 4px;
  margin-bottom: 18px;
  background: var(--panel-2);
  border: 1px solid var(--glass-border);
  border-radius: var(--radius-pill);
  backdrop-filter: blur(var(--blur));
  -webkit-backdrop-filter: blur(var(--blur));
}
.auth-screen .auth-tab {
  font: inherit;
  font-weight: 700;
  font-size: 14px;
  letter-spacing: 0.02em;
  color: var(--muted);
  background: transparent;
  border: 1px solid transparent;
  border-radius: var(--radius-pill);
  padding: 10px 12px;
  cursor: pointer;
  transition: color var(--t-fast) var(--ease),
    background var(--t) var(--ease), box-shadow var(--t) var(--ease);
}
.auth-screen .auth-tab:hover:not(.active) {
  color: var(--text);
}
.auth-screen .auth-tab.active {
  color: var(--accent-ink);
  background: linear-gradient(135deg, var(--accent), var(--accent-soft));
  box-shadow: 0 6px 18px var(--accent-glow),
    inset 0 1px 0 rgba(255, 255, 255, 0.3);
}

/* ---- "Back to log in" rail on the reset sub-flow ---- */
.auth-screen .auth-reset-back {
  font: inherit;
  font-weight: 600;
  font-size: 13px;
  align-self: flex-start;
  color: var(--accent-soft);
  background: none;
  border: none;
  padding: 4px 6px 4px 0;
  margin-bottom: 14px;
  cursor: pointer;
  border-radius: var(--radius-sm);
  transition: color var(--t-fast) var(--ease);
}
.auth-screen .auth-reset-back:hover {
  color: var(--accent);
}

/* ---- "Forgot password?" rail (log-in flow) ---- */
.auth-screen .auth-forgot-row {
  display: flex;
  justify-content: flex-end;
  margin: -4px 0 12px;
}
.auth-screen .auth-forgot {
  font-size: 13px;
}

/* ---- Inline feedback spacing inside the card ---- */
.auth-screen .auth-card .auth-error,
.auth-screen .auth-card .auth-notice {
  margin: 0 0 12px;
}

/* ---- "or" divider ---- */
.auth-screen .auth-divider {
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 16px 0 12px;
  color: var(--muted);
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.08em;
}
.auth-screen .auth-divider::before,
.auth-screen .auth-divider::after {
  content: '';
  flex: 1;
  height: 1px;
  background: var(--glass-border-strong);
}

/* ---- Google route ---- */
.auth-screen .auth-google {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  width: 100%;
}
.auth-screen .auth-google-glyph {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: #fff;
  color: #4285f4;
  font-weight: 800;
  font-size: 14px;
  line-height: 1;
}

/* ---- Fineprint under the card ---- */
.auth-screen .auth-fineprint {
  width: min(440px, 92vw);
  margin: 0;
  text-align: center;
  font-size: 13px;
  color: var(--muted);
}
`;
